import { WordElementToken } from './WordElementToken';

/**
 * A WordToken contains a single word, which can be divided in many independently
 * highlightable WordElementTokens.
 *
 */
export class WordToken {
    readonly classType: string = 'WordToken';
    #wordElementTokens: WordElementToken[] = [];

    /**
     * Builds a WordToken with a given text and highlight status.
     */
    constructor(text?: string, highlighted: boolean = false) {
        if (text) {
            this.#wordElementTokens.push(new WordElementToken(text, highlighted));
        }
    }

    /**
     * Text contained in all WordElementTokens of this word.
     */
    get text(): string {
        return this.#wordElementTokens.reduce((result, wordElementToken) => result + wordElementToken.text, '');
    }

    /**
     * Length of this word.
     */
    get length(): number {
        return this.#wordElementTokens.reduce((result, wordElementToken) => result + wordElementToken.text.length, 0);
    }

    /**
     * Contains tokens which represent parts of a word.
     */
    get wordElementTokens(): WordElementToken[] {
        return this.#wordElementTokens;
    }

    /**
     * Highlight a part of the token, potentially splitting wordElementTokens.
     */
    highlight(highlightStart: number, highlightEnd: number) {
        const newWordElementTokens: WordElementToken[] = [];
        this.#wordElementTokens.forEach((wordElementToken) => {
            const length = wordElementToken.text.length;

            if (length > highlightStart && highlightEnd > 0) {
                // The token might have to be split in up to 3 tokens.

                // add normal part before highlight start (if any)
                if (highlightStart > 0 && length >= highlightStart) {
                    newWordElementTokens.push(
                        new WordElementToken(wordElementToken.text.substring(0, highlightStart), false)
                    );
                }

                // add highlighted part
                newWordElementTokens.push(
                    new WordElementToken(wordElementToken.text.substring(highlightStart, highlightEnd), true)
                );

                // add normal part after highlight end (if any)
                if (length - highlightEnd > 0) {
                    newWordElementTokens.push(
                        new WordElementToken(wordElementToken.text.substring(highlightEnd), false)
                    );
                }
            }
            else {
                newWordElementTokens.push(wordElementToken);
            }

            // decrement highlight bounds to keep them relative to the next element's start position
            highlightStart -= length;
            highlightEnd -= length;
        });

        this.#wordElementTokens = newWordElementTokens;
    }

    /**
      * Adds a WordElementToken to the word.
      */
    add(wordElementToken: WordElementToken) {
        this.#wordElementTokens.push(wordElementToken);
    }
}
